import { Injectable } from '@angular/core';
import { Employee } from '@shared/classes/employee.class';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { Intercom, PushNotifications } from '@core/capacitor-plugins/capacitor-plugins.class';
import { PlatformService } from '../platform/platform.service';
import { Company } from '@shared/classes/company.class';
import { PAYMASH_PROFILE } from '@profile';
import { LogService } from '@core/services/system/logger';
import { LANGUAGES } from '@shared/constants';
import { AuthApiService } from '@core/services/api/auth-api.service';
import { NativeOnly } from '@shared/decorators/native-only.decor';

@Injectable()
export class IntercomService {
  private readonly logger = this.logService.createLogger('IntercomService');
  private currentRestartIdentityKey: string = null;
  private lastQueuedRestartIdentityKey: string = null;
  private restartQueue: Promise<void> = Promise.resolve();
  private restartPromisesByIdentity = new Map<string, Promise<void>>();

  constructor(
    private localStorage: LocalStorage,
    private platformService: PlatformService,
    private logService: LogService,
    private authApiService: AuthApiService
  ) {
    this.init().catch((error) => this.logger.error(error, 'init error'));
  }

  @NativeOnly()
  public async init() {
    if (this.platformService.isIOS) await PushNotifications?.register();
    if (this.platformService.isWeb) await Intercom['start'](PAYMASH_PROFILE.intercom.appId);
  }

  public async registerEmployee(company: Company, employee: Employee) {
    const userHash = await this.authApiService.getIntercomUserHash(this.platformService.currentPlatform);
    await Intercom.registerIdentifiedUser({
      userId: employee.uuid,
      email: employee.email,
    });
    await this.setUserHash(employee, userHash);

    if (this.platformService.isAndroid) {
      await Intercom.hideLauncher();
    }
    const userOptions = this.getUserOptions(company, employee);
    await this.updateUser(userOptions);
  }

  public shutdown(): Promise<void> {
    return Intercom.logout();
  }

  public show(): Promise<void> {
    return Intercom.displayMessenger();
  }

  @NativeOnly()
  public restart(company: Company, employee: Employee): Promise<void> {
    const identityKey = this.getRestartIdentityKey(company, employee);

    if (identityKey && identityKey === this.currentRestartIdentityKey && identityKey === this.lastQueuedRestartIdentityKey) {
      return Promise.resolve();
    }

    const inFlightRestart = identityKey ? this.restartPromisesByIdentity.get(identityKey) : null;
    if (inFlightRestart && identityKey === this.lastQueuedRestartIdentityKey) {
      return inFlightRestart;
    }

    const restartPromise = this.restartQueue
      .catch(() => undefined)
      .then(() => this.restartEmployee(company, employee, identityKey))
      .catch((error) => this.logger.error(error, 'Restart intercom error'))
      .then(() => undefined)
      .finally(() => {
        if (identityKey && this.restartPromisesByIdentity.get(identityKey) === restartPromise) {
          this.restartPromisesByIdentity.delete(identityKey);
        }
      });

    this.restartQueue = restartPromise.catch(() => undefined);
    this.lastQueuedRestartIdentityKey = identityKey;

    if (identityKey) {
      this.restartPromisesByIdentity.set(identityKey, restartPromise);
    }

    return restartPromise;
  }

  private async restartEmployee(company: Company, employee: Employee, identityKey: string): Promise<void> {
    if (identityKey && identityKey === this.currentRestartIdentityKey) {
      return;
    }

    await this.shutdown();
    await this.registerEmployee(company, employee);

    if (identityKey) {
      this.currentRestartIdentityKey = identityKey;
    }
  }

  private getRestartIdentityKey(company: Company, employee: Employee): string {
    const companyUuid = company?.uuid;
    const employeeUuid = employee?.uuid;

    if (!companyUuid || !employeeUuid) {
      return null;
    }

    return `${this.platformService.currentPlatform}:${companyUuid}:${employeeUuid}`;
  }

  private updateUser(_options): Promise<void> {
    return Intercom.updateUser(_options);
  }

  private setUserHash(employee: Employee, userHash: string): Promise<void> {
    if (this.platformService.isWeb) {
      const options = {
        userId: employee.uuid,
        email: employee.email,
        customAttributes: {
          user_hash: userHash,
        },
      };
      return this.updateUser(options);
    }
    return Intercom.setUserHash({ hmac: userHash });
  }

  private getUserOptions(company: Company, employee: Employee) {
    return {
      userId: employee.uuid,
      email: employee.email,
      name: employee.employeeDisplayName,
      languageOverride: LANGUAGES.de,
      customAttributes: {
        companies: [
          {
            company_id: company.uuid,
            name: company.name,
            plan: {
              type: 'plan',
              name: 'Paid',
            },
          },
        ],
        is_manager: employee?.hasAdminPermission,
        can_cancel_invoice: employee?.hasCancellationPermission,
        app_type: PAYMASH_PROFILE.intercom.appType,
        currency: company.locale.currency,
        is_restaurant_enabled: company.isRestaurantEnabled,
        vat_number: company.vatNumber,
        performance_score: this.localStorage.get('performanceTest') || null,
      },
    };
  }
}
